// src/pages/ResetPassword.jsx
// Handles PASSWORD_RECOVERY event from Supabase
// Full flow: detect token → set new password → redirect to login
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import ODFLogo from '../components/ODFLogo';
import { Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';

export default function ResetPassword({ onDone }) {
  const [pw,       setPw]      = useState('');
  const [confirm,  setConfirm] = useState('');
  const [showPw,   setShowPw]  = useState(false);
  const [loading,  setLoading] = useState(false);
  const [error,    setError]   = useState('');
  const [success,  setSuccess] = useState(false);
  const [status,   setStatus]  = useState('checking'); // checking | ready | expired
  const sessionRef = useRef(null);
  const timerRef   = useRef(null);

  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' && session) {
        sessionRef.current = session;
        setStatus('ready');
      }
      // Ignore SIGNED_OUT after success
      if (event === 'SIGNED_OUT') return;
    });

    // Check for existing session with recovery token
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      if (session) {
        sessionRef.current = session;
        setStatus('ready');
      } else {
        // Wait up to 4 seconds for PASSWORD_RECOVERY event
        timerRef.current = setTimeout(() => {
          if (mounted && status === 'checking') setStatus('expired');
        }, 4000);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line
  }, []);

  const friendlyError = msg => {
    if (!msg) return 'Something went wrong. Please try again.';
    if (msg.includes('expired') || msg.includes('invalid'))
      return 'This reset link has expired. Please request a new one.';
    if (msg.includes('Password should') || msg.includes('too short'))
      return 'Password must be at least 6 characters.';
    if (msg.includes('network') || msg.includes('Network'))
      return 'Connection error. Please try again.';
    return msg;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (pw.length < 6)    { setError('Password must be at least 6 characters.'); return; }
    if (pw !== confirm)   { setError('Passwords do not match.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setLoading(false);
    if (error) {
      setError(friendlyError(error.message));
    } else {
      setSuccess(true);
    }
  };

  const pwStr    = pw.length === 0 ? 0 : pw.length < 4 ? 1 : pw.length < 7 ? 2 : pw.length < 10 ? 3 : 4;
  const pwColors = ['#1e1e2e','#ff4d6d','#ffd166','#00b87d','#00e5a0'];
  const pwLabels = ['','Too short','Weak','Good','Strong'];
  const G = '#00e5a0';

  const inp = {
    width:'100%', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:8,
    color:'#f0f0f5', padding:'12px 14px', fontSize:14,
    fontFamily:"'IBM Plex Sans',sans-serif", outline:'none',
    boxSizing:'border-box', transition:'border-color 0.2s',
  };
  const fo = e => e.target.style.borderColor = G;
  const fb = e => e.target.style.borderColor = '#1e1e2e';

  const canSubmit = pw.length >= 6 && pw === confirm && !loading;

  return (
    <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, fontFamily:"'IBM Plex Sans',sans-serif", position:'relative' }}>
      <div style={{ position:'absolute', top:'30%', left:'50%', transform:'translate(-50%,-50%)', width:500, height:500, background:'radial-gradient(circle, rgba(0,229,160,0.05) 0%, transparent 70%)', pointerEvents:'none' }} />

      <div style={{ marginBottom:28 }}><ODFLogo size="sidebar" /></div>

      <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:16, padding:'36px 32px', width:'100%', maxWidth:400 }}>

        {/* ── CHECKING ── */}
        {status === 'checking' && (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <div style={{ width:40, height:40, borderRadius:'50%', border:'2px solid #1e1e2e', borderTopColor:G, animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }} />
            <div style={{ color:'#8888a8', fontSize:14 }}>Verifying reset link...</div>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {success && (
          <div style={{ textAlign:'center', padding:'10px 0' }}>
            <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
              <CheckCircle size={28} style={{ color:G }} />
            </div>
            <h2 style={{ fontSize:20, fontWeight:700, color:'#f0f0f5', margin:'0 0 12px' }}>Password updated!</h2>
            <p style={{ fontSize:14, color:'#8888a8', lineHeight:1.7, margin:'0 0 24px' }}>
              Your password has been changed successfully. You can now sign in with your new password.
            </p>
            <button onClick={async () => {
              // Clear URL params so recovery token is gone
              window.history.replaceState(null, '', '/');
              // Sign out to clear recovery session
              try { await supabase.auth.signOut(); } catch(e) {}
              onDone();
            }} style={{
              width:'100%', background:'#00e5a0', color:'#0a0a0f', border:'none',
              borderRadius:8, padding:'13px', fontSize:14, fontWeight:700,
              cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif",
            }}>Sign In →</button>
          </div>
        )}

        {/* ── EXPIRED / INVALID ── */}
        {status === 'expired' && !success && (
          <div style={{ textAlign:'center', padding:'10px 0' }}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:18 }}>
              <div style={{ width:52, height:52, borderRadius:14, background:'rgba(255,209,102,0.08)', border:'1px solid rgba(255,209,102,0.3)', display:'flex', alignItems:'center', justifyContent:'center', color:'#ffd166' }}><AlertTriangle size={22} strokeWidth={1.6} /></div>
            </div>
            <h2 style={{ fontSize:18, fontWeight:700, color:'#f0f0f5', margin:'0 0 10px' }}>Link expired</h2>
            <p style={{ fontSize:13, color:'#8888a8', lineHeight:1.7, margin:'0 0 24px' }}>
              This reset link has expired or already been used. Reset links are valid for 1 hour.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <button onClick={onDone} style={{
                width:'100%', background:G, color:'#0a0a0f', border:'none',
                borderRadius:8, padding:'12px', fontSize:13, fontWeight:700,
                cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif",
              }}>
                Request New Reset Link
              </button>
              <button onClick={() => window.location.href = '/'} style={{
                width:'100%', background:'transparent', color:'#8888a8',
                border:'1px solid #1e1e2e', borderRadius:8, padding:'12px',
                fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif",
                transition:'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#2a2a3e'; e.currentTarget.style.color = '#f0f0f5'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e1e2e'; e.currentTarget.style.color = '#8888a8'; }}>
                ← Back to Home
              </button>
            </div>
          </div>
        )}

        {/* ── READY — show form ── */}
        {status === 'ready' && !success && (
          <>
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Account Security</div>
              <h2 style={{ fontSize:20, fontWeight:700, color:'#f0f0f5', margin:'0 0 6px', letterSpacing:'-0.01em' }}>Set new password</h2>
              <p style={{ fontSize:13, color:'#8888a8', margin:0, lineHeight:1.6 }}>Choose a strong password for your ODFTrades account.</p>
            </div>

            <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em', display:'block', marginBottom:6 }}>New Password</label>
                <div style={{ position:'relative' }}>
                  <input type={showPw?'text':'password'} value={pw} onChange={e=>setPw(e.target.value)} placeholder="Min. 6 characters" required autoComplete="new-password" style={{ ...inp, paddingRight:44 }} onFocus={fo} onBlur={fb} />
                  <button type="button" onClick={()=>setShowPw(s=>!s)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#55556a', padding:0, lineHeight:0 }}>
                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
                {pw.length > 0 && (
                  <div style={{ marginTop:6 }}>
                    <div style={{ display:'flex', gap:4, marginBottom:3 }}>
                      {[1,2,3,4].map(i=><div key={i} style={{ flex:1, height:3, borderRadius:2, background:pwStr>=i?pwColors[pwStr]:'#1e1e2e', transition:'background 0.2s' }} />)}
                    </div>
                    <div style={{ fontSize:10, color:pwColors[pwStr], fontFamily:"'DM Mono',monospace" }}>{pwLabels[pwStr]}</div>
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em', display:'block', marginBottom:6 }}>Confirm Password</label>
                <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="••••••••" required autoComplete="new-password" style={inp} onFocus={fo} onBlur={fb} />
                {confirm.length > 0 && (
                  <div style={{ fontSize:10, marginTop:4, fontFamily:"'DM Mono',monospace", color:pw===confirm?G:'#ff4d6d' }}>
                    {pw===confirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </div>
                )}
              </div>

              {error && (
                <div style={{ fontSize:12, color:'#ff4d6d', background:'rgba(255,77,109,0.08)', border:'1px solid rgba(255,77,109,0.2)', borderRadius:7, padding:'10px 12px' }}>{error}</div>
              )}

              <button type="submit" disabled={!canSubmit} style={{
                width:'100%', background:G, color:'#0a0a0f', border:'none', borderRadius:8,
                padding:'13px', fontSize:14, fontWeight:700,
                cursor:canSubmit?'pointer':'not-allowed',
                fontFamily:"'IBM Plex Sans',sans-serif",
                opacity:canSubmit?1:0.5, transition:'all 0.2s'
              }}>
                {loading ? (
                  <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    <span style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(0,0,0,0.3)', borderTopColor:'#0a0a0f', display:'inline-block', animation:'spin 0.7s linear infinite' }} />
                    Updating...
                  </span>
                ) : 'Update Password →'}
              </button>
            </form>
          </>
        )}
      </div>

      <div style={{ fontSize:11, color:'#2a2a3e', marginTop:24, fontFamily:"'DM Mono',monospace" }}>
        ODFTrades · Your data is private and secure
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
